import { useRef, useState } from "react";
import clsx from "clsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface UploadResponse {
  ok: boolean;
  serverNowUtc: string;
  driveStartUtc: string | null;
  totalRowsParsed: number;
  totalRowsInserted: number;
  parsedMinTsUtc: string | null;
  parsedMaxTsUtc: string | null;
  rowsBeforeDriveStart: number;
  rowsAfterNow: number;
  files: { filename: string; rows: number; inserted: number }[];
}

function fmtTsUtc(iso: string | null): string {
  if (!iso) return "—";
  return iso.replace("T", " ").replace(/:\d{2}\.\d{3}Z$/, " UTC");
}

export function CsvUpload() {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);

  const mutation = useMutation({
    mutationFn: async (selected: File[]): Promise<UploadResponse> => {
      const fd = new FormData();
      for (const f of selected) fd.append("file", f);
      const res = await fetch("/api/lubrication/upload-csv", {
        method: "POST",
        body: fd,
      });
      if (!res.ok) {
        let msg = `${res.status} ${res.statusText}`;
        try {
          const j = (await res.json()) as { error?: string };
          if (j?.error) msg = j.error;
        } catch { /* swallow */ }
        throw new Error(msg);
      }
      return (await res.json()) as UploadResponse;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lubrication"] });
      setFiles([]);
      if (inputRef.current) inputRef.current.value = "";
    },
  });

  return (
    <section className="card px-5 py-4 space-y-3">
      <div>
        <h3 className="text-[12.5px] font-semibold tracking-[0.16em] uppercase text-text">
          CSV upload
        </h3>
        <p className="text-[11.5px] text-muted mt-1 leading-snug">
          Drop "Bentonite&nbsp;N.csv" exports here. Times are read as UK
          local time (BST/GMT).
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          multiple
          onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
          className="text-[12px] text-text file:mr-3 file:rounded file:border file:border-border1 file:bg-surface2 file:px-3 file:py-1.5 file:text-[11px] file:uppercase file:tracking-wider file:text-text hover:file:border-wb"
        />
        <button
          onClick={() => mutation.mutate(files)}
          disabled={files.length === 0 || mutation.isPending}
          className={clsx(
            "ml-auto rounded px-3 py-1.5 text-[11px] uppercase tracking-widest transition-colors",
            files.length === 0 || mutation.isPending
              ? "bg-border1 text-dim cursor-not-allowed"
              : "bg-wb hover:bg-wbHot text-white",
          )}
        >
          {mutation.isPending
            ? "Uploading…"
            : `Upload${files.length > 0 ? ` (${files.length})` : ""}`}
        </button>
      </div>

      {mutation.isSuccess && <UploadResult data={mutation.data} />}
      {mutation.isError && (
        <div className="text-[12px] text-wb">
          {(mutation.error as Error).message}
        </div>
      )}
    </section>
  );
}

function UploadResult({ data }: { data: UploadResponse }) {
  const dropped = data.rowsBeforeDriveStart + data.rowsAfterNow;
  const hidden = dropped > 0;
  return (
    <div className="space-y-2">
      <div className="text-[12px] text-text">
        Inserted {data.totalRowsInserted} of {data.totalRowsParsed} rows.
      </div>
      <div className="text-[11px] text-muted leading-snug">
        Parsed range:{" "}
        <span className="mono-nums">{fmtTsUtc(data.parsedMinTsUtc)}</span> →{" "}
        <span className="mono-nums">{fmtTsUtc(data.parsedMaxTsUtc)}</span>
        {data.driveStartUtc && (
          <>
            {" · "}drive start{" "}
            <span className="mono-nums">{fmtTsUtc(data.driveStartUtc)}</span>
          </>
        )}
        {" · "}server now{" "}
        <span className="mono-nums">{fmtTsUtc(data.serverNowUtc)}</span>
      </div>
      {hidden && (
        <div className="text-[11.5px] text-wb leading-snug">
          {data.rowsAfterNow > 0 && (
            <>
              {data.rowsAfterNow} row{data.rowsAfterNow === 1 ? "" : "s"}{" "}
              landed in the future — the graphs filter{" "}
              <code>ts &lt; now()</code> so these are hidden.
              <br />
            </>
          )}
          {data.rowsBeforeDriveStart > 0 && (
            <>
              {data.rowsBeforeDriveStart} row
              {data.rowsBeforeDriveStart === 1 ? "" : "s"} landed before the
              drive start date — the graphs filter{" "}
              <code>ts &gt;= driveStart</code> so these are hidden.
            </>
          )}
        </div>
      )}
    </div>
  );
}
